import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Types matching LandingPage
interface Detection {
  class_id: number;
  class_name: string;
  confidence: number;
  bbox: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  size?: {
    width_px: number;
    height_px: number;
    diameter_px: number;
    diameter_um: number;
  };
}

interface SizeStatistics {
  mean_um: number | null;
  median_um: number | null;
  mode_um: number | null;
  std_um: number | null;
  min_um: number | null;
  max_um: number | null;
  count: number;
}

interface InferenceResult {
  filename: string;
  image_size: { width: number; height: number };
  results: {
    total_detections: number;
    class_counts: Record<string, number>;
    detections: Detection[];
    statistics?: {
      overall: SizeStatistics;
      by_class: Record<string, SizeStatistics>;
      all_sizes_um: number[];
    };
    histogram?: string;
  };
  annotated_image?: string;
  base_image?: string;
}

interface UploadedFile {
  file: File;
  id: string;
  status: 'pending' | 'processing' | 'done' | 'error';
  result?: InferenceResult;
  error?: string;
}

interface BatchHistogram {
  histogram: string;
  stats: {
    count: number;
    mean: number;
    std: number;
    median: number;
    min: number;
    max: number;
  };
}

interface BatchAnalysis {
  totalImages: number;
  totalDetections: number;
  classCounts: Record<string, number>;
  statsByClass: Record<string, {
    count: number;
    mean_um: number | null;
    median_um: number | null;
    std_um: number | null;
    min_um: number | null;
    max_um: number | null;
    shape_metrics: { roundness: number; convexity: number; aspect_ratio: number; solidity: number } | null;
  }>;
}

export interface ExportOptions {
  pdf: boolean;
  pdfIncludeIndividualImages: boolean;
  pdfIncludePerImageHistograms: boolean;
  excel: boolean;
  csv: boolean;
  detectionImages: boolean;
}

export interface ExportData {
  processedFiles: UploadedFile[];
  batchAnalysis: BatchAnalysis;
  batchHistograms: Record<string, BatchHistogram>;
  exclusionsByFile: Record<string, Set<number>>;
  // Regenerated per-image histograms: fileId -> className -> base64 histogram
  regeneratedHistogramsByFile: Record<string, Record<string, string>>;
  pixelToUm: number;
  confThreshold: number;
  // Size filter for exports
  sizeFilter?: { min: number; max: number };
}

// Helper: Get active detections (excluding excluded ones and filtered by size)
function getActiveDetections(
  file: UploadedFile,
  exclusionsByFile: Record<string, Set<number>>,
  sizeFilter?: { min: number; max: number }
): Detection[] {
  if (!file.result) return [];
  const excludedIndices = exclusionsByFile[file.id] || new Set<number>();
  return file.result.results.detections.filter((det, index) => {
    // Skip excluded
    if (excludedIndices.has(index)) return false;
    // Skip outside size range
    if (sizeFilter && det.size?.diameter_um) {
      if (det.size.diameter_um < sizeFilter.min || det.size.diameter_um > sizeFilter.max) {
        return false;
      }
    }
    return true;
  });
}

// Class colors matching the backend
const CLASS_COLORS: Record<string, string> = {
  dense_sphere: '#3b82f6',
  hollow_sphere: '#22c55e',
  'non_sphere-broken': '#f97316',
  polymer: '#ef4444',
};

// Helper: Compress image for PDF export (resize to 1200px max width, convert to JPEG 85%)
async function compressImageForPDF(base64Image: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(base64Image); // Fallback to original if canvas fails
        return;
      }

      // Calculate new dimensions (max width 1200px, maintain aspect ratio)
      const maxWidth = 1200;
      let newWidth = img.width;
      let newHeight = img.height;

      if (img.width > maxWidth) {
        newWidth = maxWidth;
        newHeight = Math.round((img.height * maxWidth) / img.width);
      }

      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw resized image
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert to JPEG at 85% quality
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };

    img.onerror = () => {
      resolve(base64Image); // Fallback to original on error
    };

    img.src = `data:image/png;base64,${base64Image}`;
  });
}

// Helper: Generate annotated image with exclusions and size filter applied
async function generateAnnotatedImageWithExclusions(
  file: UploadedFile,
  excludedIndices: Set<number>,
  sizeFilter?: { min: number; max: number }
): Promise<string | null> {
  if (!file.result?.base_image) return file.result?.annotated_image || null;

  // If no exclusions and no size filter, use original annotated image
  if (excludedIndices.size === 0 && !sizeFilter) {
    return file.result.annotated_image || null;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(file.result?.annotated_image || null);
        return;
      }

      // Draw base image
      ctx.drawImage(img, 0, 0);

      // Draw only active detections (not excluded, within size range)
      const detections = file.result!.results.detections;
      detections.forEach((det, index) => {
        if (excludedIndices.has(index)) return; // Skip excluded

        // Skip detections outside size filter range
        if (sizeFilter && det.size?.diameter_um) {
          if (det.size.diameter_um < sizeFilter.min || det.size.diameter_um > sizeFilter.max) {
            return;
          }
        }

        const color = CLASS_COLORS[det.class_name] || '#888888';
        const { x1, y1, x2, y2 } = det.bbox;

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      });

      // Convert to base64
      const dataUrl = canvas.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };

    img.onerror = () => {
      resolve(file.result?.annotated_image || null);
    };

    img.src = `data:image/png;base64,${file.result!.base_image}`;
  });
}

// ===== PDF Styling Constants =====
const PDF_COLORS = {
  primary: { r: 255, g: 102, b: 0 },      // #ff6600 - Orange
  darkOrange: { r: 204, g: 82, b: 0 },    // #cc5200 - Dark Orange (for gradient end)
  veryLightOrange: { r: 255, g: 204, b: 153 }, // #ffcc99 - Very Light Orange (for gradient start - makes AI text visible)
  black: { r: 0, g: 0, b: 0 },             // #000000
  white: { r: 255, g: 255, b: 255 },       // #ffffff
  lightGray: { r: 245, g: 245, b: 245 },   // #f5f5f5 - Light background
  mediumGray: { r: 229, g: 229, b: 229 },  // #e5e5e5 - Table headers
  darkGray: { r: 64, g: 64, b: 64 },       // #404040 - Secondary text
};

// Persist AI Logo placeholder - will be loaded dynamically
let persistLogoBase64: string | null = null;

// Helper: Load Persist AI logo for PDF
async function loadPersistLogo(): Promise<string | null> {
  if (persistLogoBase64) return persistLogoBase64;

  try {
    // Use the full black logo for white header background
    const response = await fetch('/assets/persist-logo-black.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        persistLogoBase64 = reader.result as string;
        resolve(persistLogoBase64);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error('Failed to load Persist logo:', e);
    return null;
  }
}

// Helper: Draw page header with white background and orange bottom border
function drawPageHeader(pdf: jsPDF, title: string, pageWidth: number, logoBase64?: string | null, showLogo: boolean = false) {
  const headerHeight = 20;
  pdf.setFillColor(PDF_COLORS.white.r, PDF_COLORS.white.g, PDF_COLORS.white.b);
  pdf.rect(0, 0, pageWidth, headerHeight, 'F');

  // Orange border at bottom of header
  pdf.setDrawColor(PDF_COLORS.primary.r, PDF_COLORS.primary.g, PDF_COLORS.primary.b);
  pdf.setLineWidth(1);
  pdf.line(0, headerHeight, pageWidth, headerHeight);

  // Subtle gray shadow line
  pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.setLineWidth(0.3);
  pdf.line(0, headerHeight + 0.7, pageWidth, headerHeight + 0.7);

  // Persist AI full logo on the left (only on first page)
  if (showLogo && logoBase64) {
    try {
      const logoHeight = 10;
      const logoWidth = 42;
      const logoX = 2;
      const logoY = 5;
      pdf.addImage(logoBase64, logoX, logoY, logoWidth, logoHeight);
    } catch (e) {
      console.error('Failed to add logo to PDF header:', e);
    }
  }

  // Black title text centered
  pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, pageWidth / 2, 13, { align: 'center' });

  // Reset text color
  pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
}

// Helper: Draw page footer with page number
function drawPageFooter(pdf: jsPDF, pageNum: number, totalPages: number, pageWidth: number, pageHeight: number) {
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(PDF_COLORS.darkGray.r, PDF_COLORS.darkGray.g, PDF_COLORS.darkGray.b);
  pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
}

// Helper: Draw section header with background
function drawSectionHeader(pdf: jsPDF, title: string, y: number, margin: number, pageWidth: number): number {
  const sectionHeight = 8;

  // Light gray background
  pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
  pdf.rect(margin, y, pageWidth - 2 * margin, sectionHeight, 'F');

  // Orange left accent bar
  pdf.setFillColor(PDF_COLORS.primary.r, PDF_COLORS.primary.g, PDF_COLORS.primary.b);
  pdf.rect(margin, y, 3, sectionHeight, 'F');

  // Section title
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
  pdf.text(title, margin + 6, y + 5.5);

  return y + sectionHeight + 4;
}

// Helper: Draw metric card
function drawMetricCard(pdf: jsPDF, value: string, label: string, x: number, y: number, width: number, height: number) {
  // Card background
  pdf.setFillColor(PDF_COLORS.white.r, PDF_COLORS.white.g, PDF_COLORS.white.b);
  pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.setLineWidth(0.5);
  pdf.rect(x, y, width, height, 'FD');

  // Orange top accent
  pdf.setFillColor(PDF_COLORS.primary.r, PDF_COLORS.primary.g, PDF_COLORS.primary.b);
  pdf.rect(x, y, width, 2, 'F');

  // Value (large)
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(PDF_COLORS.primary.r, PDF_COLORS.primary.g, PDF_COLORS.primary.b);
  pdf.text(value, x + width / 2, y + 14, { align: 'center' });

  // Label (small)
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(PDF_COLORS.darkGray.r, PDF_COLORS.darkGray.g, PDF_COLORS.darkGray.b);
  pdf.text(label, x + width / 2, y + 21, { align: 'center' });

  // Reset
  pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
}

// Helper: Draw a styled table
function drawTable(
  pdf: jsPDF,
  headers: string[],
  rows: (string | number)[][],
  x: number,
  y: number,
  colWidths: number[],
  options?: { colorDots?: Record<number, string> }
): number {
  const rowHeight = 7;
  const headerHeight = 8;
  const padding = 2;
  const tableWidth = colWidths.reduce((a, b) => a + b, 0);

  // Header background
  pdf.setFillColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.rect(x, y, tableWidth, headerHeight, 'F');

  // Header text
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);

  let colX = x;
  headers.forEach((header, i) => {
    pdf.text(header, colX + padding, y + 5.5);
    colX += colWidths[i];
  });

  y += headerHeight;

  // Data rows
  pdf.setFont('helvetica', 'normal');
  rows.forEach((row, rowIndex) => {
    // Alternating row background
    if (rowIndex % 2 === 1) {
      pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
      pdf.rect(x, y, tableWidth, rowHeight, 'F');
    }

    colX = x;
    row.forEach((cell, colIndex) => {
      const cellText = String(cell);

      // Draw color dot if specified for first column
      if (colIndex === 0 && options?.colorDots && options.colorDots[rowIndex]) {
        const color = options.colorDots[rowIndex];
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        pdf.setFillColor(r, g, b);
        pdf.circle(colX + padding + 1.5, y + 3.5, 1.5, 'F');
        pdf.text(cellText, colX + padding + 5, y + 5);
      } else {
        pdf.text(cellText, colX + padding, y + 5);
      }
      colX += colWidths[colIndex];
    });

    y += rowHeight;
  });

  // Table border
  pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y - rows.length * rowHeight - headerHeight, tableWidth, rows.length * rowHeight + headerHeight, 'S');

  return y + 2;
}

// Helper: Draw progress bar for percentages
function drawProgressBar(pdf: jsPDF, percentage: number, x: number, y: number, width: number, height: number, color: string) {
  // Background
  pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
  pdf.rect(x, y, width, height, 'F');

  // Fill
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  pdf.setFillColor(r, g, b);
  pdf.rect(x, y, width * (percentage / 100), height, 'F');
}

// ===== PDF Generation =====
export async function generatePDF(
  data: ExportData,
  options: ExportOptions,
  onProgress?: (message: string, current?: number, total?: number) => void
): Promise<Blob> {
  onProgress?.('Initializing PDF generation...');

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = 25; // Start below header

  // Load Persist AI logo for PDF header
  const logoBase64 = await loadPersistLogo();

  onProgress?.('Creating summary page...');

  // ===== PAGE 1: Summary =====
  drawPageHeader(pdf, 'AI MICROPARTICLE ANALYSIS REPORT', pageWidth, logoBase64, true);

  // Subtitle with date and parameters
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(PDF_COLORS.darkGray.r, PDF_COLORS.darkGray.g, PDF_COLORS.darkGray.b);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  pdf.text(`Confidence: ${(data.confThreshold * 100).toFixed(0)}%  |  Calibration: ${data.pixelToUm} um/pixel`, pageWidth / 2, yPos, { align: 'center' });
  pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
  yPos += 10;

  // Metric Cards Row
  const cardWidth = (contentWidth - 10) / 3;
  const cardHeight = 26;
  drawMetricCard(pdf, String(data.batchAnalysis.totalImages), 'Images Analyzed', margin, yPos, cardWidth, cardHeight);
  drawMetricCard(pdf, String(data.batchAnalysis.totalDetections), 'Total Detections', margin + cardWidth + 5, yPos, cardWidth, cardHeight);
  drawMetricCard(pdf, String(Object.keys(data.batchAnalysis.classCounts).length), 'Classes Found', margin + 2 * (cardWidth + 5), yPos, cardWidth, cardHeight);
  yPos += cardHeight + 8;

  // Class Distribution Section
  yPos = drawSectionHeader(pdf, 'CLASS DISTRIBUTION', yPos, margin, pageWidth);

  // Class distribution table with color dots and progress bars
  const classDistHeaders = ['Class', 'Count', '%', 'Distribution'];
  const classDistRows: (string | number)[][] = [];
  const colorDots: Record<number, string> = {};

  Object.entries(data.batchAnalysis.classCounts).forEach(([className, count], index) => {
    const percentage = ((count / data.batchAnalysis.totalDetections) * 100).toFixed(1);
    classDistRows.push([className.replace(/_/g, ' '), count, `${percentage}%`, '']);
    colorDots[index] = CLASS_COLORS[className] || '#888888';
  });

  // Draw custom class distribution table
  const distRowHeight = 8;
  const distColWidths = [55, 25, 20, 80];
  const distTableWidth = distColWidths.reduce((a, b) => a + b, 0);

  // Header
  pdf.setFillColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.rect(margin, yPos, distTableWidth, 8, 'F');
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  let colX = margin;
  classDistHeaders.forEach((header, i) => {
    pdf.text(header, colX + 2, yPos + 5.5);
    colX += distColWidths[i];
  });
  yPos += 8;

  // Rows with progress bars
  pdf.setFont('helvetica', 'normal');
  Object.entries(data.batchAnalysis.classCounts).forEach(([className, count], rowIndex) => {
    const percentage = (count / data.batchAnalysis.totalDetections) * 100;

    // Alternating background
    if (rowIndex % 2 === 1) {
      pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
      pdf.rect(margin, yPos, distTableWidth, distRowHeight, 'F');
    }

    // Color dot + Class name
    const color = CLASS_COLORS[className] || '#888888';
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    pdf.setFillColor(r, g, b);
    pdf.circle(margin + 4, yPos + 4, 2, 'F');
    pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
    pdf.text(className.replace(/_/g, ' '), margin + 9, yPos + 5.5);

    // Count
    pdf.text(String(count), margin + distColWidths[0] + 2, yPos + 5.5);

    // Percentage
    pdf.text(`${percentage.toFixed(1)}%`, margin + distColWidths[0] + distColWidths[1] + 2, yPos + 5.5);

    // Progress bar
    const barX = margin + distColWidths[0] + distColWidths[1] + distColWidths[2] + 4;
    const barWidth = distColWidths[3] - 8;
    drawProgressBar(pdf, percentage, barX, yPos + 2, barWidth, 4, color);

    yPos += distRowHeight;
  });

  // Table border
  pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.setLineWidth(0.3);
  pdf.rect(margin, yPos - Object.keys(data.batchAnalysis.classCounts).length * distRowHeight - 8, distTableWidth, Object.keys(data.batchAnalysis.classCounts).length * distRowHeight + 8, 'S');
  yPos += 8;

  // Size Statistics Section
  yPos = drawSectionHeader(pdf, 'SIZE STATISTICS BY CLASS', yPos, margin, pageWidth);

  // Statistics table
  const statsHeaders = ['Class', 'Count', 'Mean', 'Median', 'Std', 'Min', 'Max'];
  const statsColWidths = [45, 18, 22, 22, 22, 22, 22];
  const statsTableWidth = statsColWidths.reduce((a, b) => a + b, 0);

  // Header
  pdf.setFillColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.rect(margin, yPos, statsTableWidth, 8, 'F');
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  colX = margin;
  statsHeaders.forEach((header, i) => {
    pdf.text(header, colX + 2, yPos + 5.5);
    colX += statsColWidths[i];
  });
  yPos += 8;

  // Stats rows
  pdf.setFont('helvetica', 'normal');
  Object.entries(data.batchAnalysis.statsByClass).forEach(([className, stats], rowIndex) => {
    if (rowIndex % 2 === 1) {
      pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
      pdf.rect(margin, yPos, statsTableWidth, 7, 'F');
    }

    // Color dot + name
    const color = CLASS_COLORS[className] || '#888888';
    const cr = parseInt(color.slice(1, 3), 16);
    const cg = parseInt(color.slice(3, 5), 16);
    const cb = parseInt(color.slice(5, 7), 16);
    pdf.setFillColor(cr, cg, cb);
    pdf.circle(margin + 4, yPos + 3.5, 1.5, 'F');
    pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);

    const rowData = [
      className.replace(/_/g, ' '),
      String(stats.count),
      stats.mean_um?.toFixed(1) || '-',
      stats.median_um?.toFixed(1) || '-',
      stats.std_um?.toFixed(1) || '-',
      stats.min_um?.toFixed(1) || '-',
      stats.max_um?.toFixed(1) || '-',
    ];

    colX = margin;
    rowData.forEach((cell, i) => {
      if (i === 0) {
        pdf.text(cell, colX + 8, yPos + 5);
      } else {
        pdf.text(cell, colX + 2, yPos + 5);
      }
      colX += statsColWidths[i];
    });

    yPos += 7;
  });

  // Table border
  pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
  pdf.rect(margin, yPos - Object.keys(data.batchAnalysis.statsByClass).length * 7 - 8, statsTableWidth, Object.keys(data.batchAnalysis.statsByClass).length * 7 + 8, 'S');
  yPos += 8;

  // Shape Metrics Section (if available)
  const hasShapeMetrics = Object.values(data.batchAnalysis.statsByClass).some(s => s.shape_metrics);
  if (hasShapeMetrics) {
    yPos = drawSectionHeader(pdf, 'SHAPE CHARACTERISTICS', yPos, margin, pageWidth);

    const shapeHeaders = ['Class', 'Roundness', 'Convexity', 'Aspect Ratio', 'Solidity'];
    const shapeColWidths = [50, 30, 30, 30, 30];
    const shapeTableWidth = shapeColWidths.reduce((a, b) => a + b, 0);

    // Header
    pdf.setFillColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
    pdf.rect(margin, yPos, shapeTableWidth, 8, 'F');
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    colX = margin;
    shapeHeaders.forEach((header, i) => {
      pdf.text(header, colX + 2, yPos + 5.5);
      colX += shapeColWidths[i];
    });
    yPos += 8;

    // Shape rows
    pdf.setFont('helvetica', 'normal');
    Object.entries(data.batchAnalysis.statsByClass).forEach(([className, stats], rowIndex) => {
      if (!stats.shape_metrics) return;

      if (rowIndex % 2 === 1) {
        pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
        pdf.rect(margin, yPos, shapeTableWidth, 7, 'F');
      }

      const color = CLASS_COLORS[className] || '#888888';
      const cr = parseInt(color.slice(1, 3), 16);
      const cg = parseInt(color.slice(3, 5), 16);
      const cb = parseInt(color.slice(5, 7), 16);
      pdf.setFillColor(cr, cg, cb);
      pdf.circle(margin + 4, yPos + 3.5, 1.5, 'F');
      pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);

      const shapeData = [
        className.replace(/_/g, ' '),
        stats.shape_metrics.roundness.toFixed(3),
        stats.shape_metrics.convexity.toFixed(3),
        stats.shape_metrics.aspect_ratio.toFixed(3),
        stats.shape_metrics.solidity.toFixed(3),
      ];

      colX = margin;
      shapeData.forEach((cell, i) => {
        if (i === 0) {
          pdf.text(cell, colX + 8, yPos + 5);
        } else {
          pdf.text(cell, colX + 2, yPos + 5);
        }
        colX += shapeColWidths[i];
      });

      yPos += 7;
    });

    pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
    const shapeRowCount = Object.values(data.batchAnalysis.statsByClass).filter(s => s.shape_metrics).length;
    pdf.rect(margin, yPos - shapeRowCount * 7 - 8, shapeTableWidth, shapeRowCount * 7 + 8, 'S');
  }

  // Batch Histograms
  if (Object.keys(data.batchHistograms).length > 0) {
    onProgress?.('Adding batch histograms...');

    pdf.addPage();
    drawPageHeader(pdf, 'SIZE DISTRIBUTION HISTOGRAMS', pageWidth, logoBase64);
    yPos = 28;

    const histogramWidth = 85;
    const histogramHeight = 60;
    let xPos = margin;

    for (const [className, histData] of Object.entries(data.batchHistograms)) {
      if (xPos + histogramWidth > pageWidth - margin) {
        xPos = margin;
        yPos += histogramHeight + 18;
      }
      if (yPos + histogramHeight > pageHeight - 20) {
        pdf.addPage();
        drawPageHeader(pdf, 'SIZE DISTRIBUTION HISTOGRAMS', pageWidth, logoBase64);
        yPos = 28;
        xPos = margin;
      }

      // Histogram card with border
      pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
      pdf.setLineWidth(0.3);
      pdf.rect(xPos - 2, yPos - 2, histogramWidth + 4, histogramHeight + 14, 'S');

      // Color indicator + class name
      const color = CLASS_COLORS[className] || '#888888';
      const cr = parseInt(color.slice(1, 3), 16);
      const cg = parseInt(color.slice(3, 5), 16);
      const cb = parseInt(color.slice(5, 7), 16);
      pdf.setFillColor(cr, cg, cb);
      pdf.circle(xPos + 2, yPos + 3, 2, 'F');

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
      pdf.text(`${className.replace(/_/g, ' ')} (n=${histData.stats.count})`, xPos + 6, yPos + 4);

      try {
        pdf.addImage(`data:image/png;base64,${histData.histogram}`, 'PNG', xPos, yPos + 8, histogramWidth, histogramHeight);
      } catch (e) {
        console.error('Failed to add histogram image:', e);
      }

      xPos += histogramWidth + 10;
    }
  }

  // Individual Images (optional)
  if (options.pdfIncludeIndividualImages) {
    const totalImages = data.processedFiles.filter(f => f.result).length;
    let processedImages = 0;

    for (const file of data.processedFiles) {
      if (!file.result) continue;

      processedImages++;
      onProgress?.(`Processing image ${processedImages}/${totalImages}`, processedImages, totalImages);

      pdf.addPage();
      drawPageHeader(pdf, 'IMAGE ANALYSIS DETAILS', pageWidth, logoBase64);
      yPos = 28;

      // Image filename section header
      yPos = drawSectionHeader(pdf, `Image: ${file.result.filename}`, yPos, margin, pageWidth);

      const activeDetections = getActiveDetections(file, data.exclusionsByFile, data.sizeFilter);
      const excludedIndices = data.exclusionsByFile[file.id] || new Set<number>();

      // Add annotated image (with exclusions and size filter applied)
      const annotatedImage = await generateAnnotatedImageWithExclusions(file, excludedIndices, data.sizeFilter);
      if (annotatedImage) {
        // Compress image for PDF (resize to 1200px max width, convert to JPEG 85%)
        const compressedImage = await compressImageForPDF(annotatedImage);

        const imgWidth = 170;
        const imgHeight = 120;
        try {
          // Image border
          pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
          pdf.setLineWidth(0.5);
          pdf.rect(margin - 1, yPos - 1, imgWidth + 2, imgHeight + 2, 'S');

          pdf.addImage(
            `data:image/jpeg;base64,${compressedImage}`,
            'JPEG',
            margin,
            yPos,
            imgWidth,
            imgHeight
          );
          yPos += imgHeight + 8;
        } catch (e) {
          console.error('Failed to add annotated image:', e);
        }
      }

      // Per-class breakdown for this image
      const classCounts: Record<string, number> = {};
      const sizesByClass: Record<string, number[]> = {};
      activeDetections.forEach(det => {
        classCounts[det.class_name] = (classCounts[det.class_name] || 0) + 1;
        if (det.size?.diameter_um) {
          if (!sizesByClass[det.class_name]) sizesByClass[det.class_name] = [];
          sizesByClass[det.class_name].push(det.size.diameter_um);
        }
      });

      // Get shape metrics from file statistics or batch analysis
      const fileStats = file.result.results.statistics;
      const shapeMetricsByClass: Record<string, { roundness: number; convexity: number; aspect_ratio: number; solidity: number } | null> = {};

      // First try to get from file-level statistics
      if (fileStats?.by_class) {
        Object.entries(fileStats.by_class).forEach(([className, stats]: [string, any]) => {
          if (stats.shape_metrics && classCounts[className]) {
            shapeMetricsByClass[className] = stats.shape_metrics;
          }
        });
      }

      // If no file-level shape metrics, fall back to batch analysis shape metrics
      if (Object.keys(shapeMetricsByClass).length === 0) {
        Object.entries(data.batchAnalysis.statsByClass).forEach(([className, stats]) => {
          if (stats.shape_metrics && classCounts[className]) {
            shapeMetricsByClass[className] = stats.shape_metrics;
          }
        });
      }

      // Calculate per-image statistics by class
      const perImageStats: Record<string, { count: number; mean: number | null; median: number | null; std: number | null; min: number | null; max: number | null }> = {};
      Object.entries(sizesByClass).forEach(([className, sizes]) => {
        if (sizes.length === 0) {
          perImageStats[className] = { count: classCounts[className], mean: null, median: null, std: null, min: null, max: null };
          return;
        }
        const mean = sizes.reduce((a, b) => a + b, 0) / sizes.length;
        const sorted = [...sizes].sort((a, b) => a - b);
        const median = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)];
        const std = sizes.length > 1
          ? Math.sqrt(sizes.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / (sizes.length - 1))
          : null;
        perImageStats[className] = {
          count: classCounts[className],
          mean,
          median,
          std,
          min: Math.min(...sizes),
          max: Math.max(...sizes),
        };
      });

      // Add classes with counts but no sizes
      Object.keys(classCounts).forEach(className => {
        if (!perImageStats[className]) {
          perImageStats[className] = { count: classCounts[className], mean: null, median: null, std: null, min: null, max: null };
        }
      });

      // Total detections header
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Total Detections: ${activeDetections.length}`, margin, yPos);
      yPos += 8;

      // CLASS DISTRIBUTION TABLE (Priority 1)
      if (Object.keys(classCounts).length > 0) {
        yPos = drawSectionHeader(pdf, 'CLASS DISTRIBUTION', yPos, margin, pageWidth);

        const distHeaders = ['Class', 'Count', '%', 'Distribution'];
        const distColWidths = [55, 25, 20, 80];
        const distTableWidth = distColWidths.reduce((a, b) => a + b, 0);

        // Header
        pdf.setFillColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
        pdf.rect(margin, yPos, distTableWidth, 8, 'F');
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        let colX = margin;
        distHeaders.forEach((header, i) => {
          pdf.text(header, colX + 2, yPos + 5.5);
          colX += distColWidths[i];
        });
        yPos += 8;

        // Rows with progress bars
        const totalDetections = activeDetections.length;
        pdf.setFont('helvetica', 'normal');
        Object.entries(classCounts).forEach(([className, count], rowIndex) => {
          const percentage = totalDetections > 0 ? (count / totalDetections) * 100 : 0;

          // Alternating background
          if (rowIndex % 2 === 1) {
            pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
            pdf.rect(margin, yPos, distTableWidth, 8, 'F');
          }

          // Color dot + Class name
          const color = CLASS_COLORS[className] || '#888888';
          const cr = parseInt(color.slice(1, 3), 16);
          const cg = parseInt(color.slice(3, 5), 16);
          const cb = parseInt(color.slice(5, 7), 16);
          pdf.setFillColor(cr, cg, cb);
          pdf.circle(margin + 4, yPos + 4, 2, 'F');
          pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
          pdf.text(className.replace(/_/g, ' '), margin + 9, yPos + 5.5);

          // Count
          pdf.text(String(count), margin + distColWidths[0] + 2, yPos + 5.5);

          // Percentage
          pdf.text(`${percentage.toFixed(1)}%`, margin + distColWidths[0] + distColWidths[1] + 2, yPos + 5.5);

          // Progress bar
          const barX = margin + distColWidths[0] + distColWidths[1] + distColWidths[2] + 4;
          const barWidth = distColWidths[3] - 8;
          drawProgressBar(pdf, percentage, barX, yPos + 2, barWidth, 4, color);

          yPos += 8;
        });

        // Table border
        pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
        pdf.setLineWidth(0.3);
        pdf.rect(margin, yPos - Object.keys(classCounts).length * 8 - 8, distTableWidth, Object.keys(classCounts).length * 8 + 8, 'S');
        yPos += 6;
      }

      // SIZE STATISTICS TABLE (Priority 2)
      if (Object.keys(perImageStats).length > 0 && yPos < pageHeight - 50) {
        yPos = drawSectionHeader(pdf, 'SIZE STATISTICS', yPos, margin, pageWidth);

        const statsHeaders = ['Class', 'Count', 'Mean', 'Median', 'Std', 'Min', 'Max'];
        const statsColWidths = [45, 18, 22, 22, 22, 22, 22];
        const statsTableWidth = statsColWidths.reduce((a, b) => a + b, 0);

        // Header
        pdf.setFillColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
        pdf.rect(margin, yPos, statsTableWidth, 8, 'F');
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        let colX = margin;
        statsHeaders.forEach((header, i) => {
          pdf.text(header, colX + 2, yPos + 5.5);
          colX += statsColWidths[i];
        });
        yPos += 8;

        // Stats rows
        pdf.setFont('helvetica', 'normal');
        Object.entries(perImageStats).forEach(([className, stats], rowIndex) => {
          if (rowIndex % 2 === 1) {
            pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
            pdf.rect(margin, yPos, statsTableWidth, 7, 'F');
          }

          // Color dot + name
          const color = CLASS_COLORS[className] || '#888888';
          const cr = parseInt(color.slice(1, 3), 16);
          const cg = parseInt(color.slice(3, 5), 16);
          const cb = parseInt(color.slice(5, 7), 16);
          pdf.setFillColor(cr, cg, cb);
          pdf.circle(margin + 4, yPos + 3.5, 1.5, 'F');
          pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);

          const rowData = [
            className.replace(/_/g, ' '),
            String(stats.count),
            stats.mean?.toFixed(1) || '-',
            stats.median?.toFixed(1) || '-',
            stats.std?.toFixed(1) || '-',
            stats.min?.toFixed(1) || '-',
            stats.max?.toFixed(1) || '-',
          ];

          colX = margin;
          rowData.forEach((cell, i) => {
            if (i === 0) {
              pdf.text(cell, colX + 8, yPos + 5);
            } else {
              pdf.text(cell, colX + 2, yPos + 5);
            }
            colX += statsColWidths[i];
          });

          yPos += 7;
        });

        // Table border
        pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
        pdf.rect(margin, yPos - Object.keys(perImageStats).length * 7 - 8, statsTableWidth, Object.keys(perImageStats).length * 7 + 8, 'S');
        yPos += 6;
      }

      // SHAPE CHARACTERISTICS TABLE (Priority 3)
      const classesWithShapeMetrics = Object.entries(shapeMetricsByClass).filter(([_, metrics]) => metrics !== null);
      if (classesWithShapeMetrics.length > 0) {
        // Calculate required height for shape table: header (12) + section header (12) + rows (7 each) + border spacing (8)
        const shapeTableHeight = 12 + 8 + classesWithShapeMetrics.length * 7 + 8;

        // If not enough space, start new page
        if (yPos + shapeTableHeight > pageHeight - 20) {
          pdf.addPage();
          drawPageHeader(pdf, 'IMAGE ANALYSIS DETAILS', pageWidth, logoBase64);
          yPos = 28;
        }

        yPos = drawSectionHeader(pdf, 'SHAPE CHARACTERISTICS', yPos, margin, pageWidth);

        const shapeHeaders = ['Class', 'Roundness', 'Convexity', 'Aspect Ratio', 'Solidity'];
        const shapeColWidths = [50, 30, 30, 30, 30];
        const shapeTableWidth = shapeColWidths.reduce((a, b) => a + b, 0);

        // Header
        pdf.setFillColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
        pdf.rect(margin, yPos, shapeTableWidth, 8, 'F');
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        let colX = margin;
        shapeHeaders.forEach((header, i) => {
          pdf.text(header, colX + 2, yPos + 5.5);
          colX += shapeColWidths[i];
        });
        yPos += 8;

        // Shape rows
        pdf.setFont('helvetica', 'normal');
        classesWithShapeMetrics.forEach(([className, metrics], rowIndex) => {
          if (!metrics) return;

          if (rowIndex % 2 === 1) {
            pdf.setFillColor(PDF_COLORS.lightGray.r, PDF_COLORS.lightGray.g, PDF_COLORS.lightGray.b);
            pdf.rect(margin, yPos, shapeTableWidth, 7, 'F');
          }

          const color = CLASS_COLORS[className] || '#888888';
          const cr = parseInt(color.slice(1, 3), 16);
          const cg = parseInt(color.slice(3, 5), 16);
          const cb = parseInt(color.slice(5, 7), 16);
          pdf.setFillColor(cr, cg, cb);
          pdf.circle(margin + 4, yPos + 3.5, 1.5, 'F');
          pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);

          const shapeData = [
            className.replace(/_/g, ' '),
            metrics.roundness.toFixed(3),
            metrics.convexity.toFixed(3),
            metrics.aspect_ratio.toFixed(3),
            metrics.solidity.toFixed(3),
          ];

          colX = margin;
          shapeData.forEach((cell, i) => {
            if (i === 0) {
              pdf.text(cell, colX + 8, yPos + 5);
            } else {
              pdf.text(cell, colX + 2, yPos + 5);
            }
            colX += shapeColWidths[i];
          });

          yPos += 7;
        });

        pdf.setDrawColor(PDF_COLORS.mediumGray.r, PDF_COLORS.mediumGray.g, PDF_COLORS.mediumGray.b);
        pdf.rect(margin, yPos - classesWithShapeMetrics.length * 7 - 8, shapeTableWidth, classesWithShapeMetrics.length * 7 + 8, 'S');
        yPos += 6;
      }

      // Per-image histograms (optional)
      if (options.pdfIncludePerImageHistograms && file.result.results.statistics?.by_class) {
        const byClass = file.result.results.statistics.by_class as Record<string, any>;
        const regeneratedHistograms = data.regeneratedHistogramsByFile[file.id] || {};

        // Only include classes that have active detections (after exclusions/size filter)
        const classesWithHistograms = Object.entries(byClass).filter(([className, stats]) => {
          const hasActiveDetections = (classCounts[className] || 0) > 0;
          return hasActiveDetections && (stats.histogram || regeneratedHistograms[className]);
        });

        if (classesWithHistograms.length > 0) {
          if (yPos > pageHeight - 80) {
            pdf.addPage();
            drawPageHeader(pdf, 'IMAGE ANALYSIS DETAILS', pageWidth, logoBase64);
            yPos = 28;
          }

          yPos += 3;
          yPos = drawSectionHeader(pdf, 'Per-Class Size Distributions', yPos, margin, pageWidth);

          const histWidth = 85;
          const histHeight = 55;
          let xPos = margin;

          for (const [className, stats] of classesWithHistograms) {
            const histogramToUse = regeneratedHistograms[className] || stats.histogram;

            if (histogramToUse) {
              if (xPos + histWidth > pageWidth - margin) {
                xPos = margin;
                yPos += histHeight + 12;
              }
              if (yPos + histHeight > pageHeight - 20) {
                pdf.addPage();
                drawPageHeader(pdf, 'IMAGE ANALYSIS DETAILS', pageWidth, logoBase64);
                yPos = 28;
                xPos = margin;
              }

              // Color dot + label
              const color = CLASS_COLORS[className] || '#888888';
              const cr = parseInt(color.slice(1, 3), 16);
              const cg = parseInt(color.slice(3, 5), 16);
              const cb = parseInt(color.slice(5, 7), 16);
              pdf.setFillColor(cr, cg, cb);
              pdf.circle(xPos + 2, yPos + 2, 1.5, 'F');

              pdf.setFontSize(8);
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(PDF_COLORS.black.r, PDF_COLORS.black.g, PDF_COLORS.black.b);
              pdf.text(className.replace(/_/g, ' '), xPos + 5, yPos + 3);

              try {
                pdf.addImage(`data:image/png;base64,${histogramToUse}`, 'PNG', xPos, yPos + 6, histWidth, histHeight);
              } catch (e) {
                console.error(`Failed to add histogram for ${className}:`, e);
              }

              xPos += histWidth + 5;
            }
          }
          yPos += histHeight + 15;
        }
      }
    }
  }

  // Add page numbers to all pages
  onProgress?.('Finalizing PDF...');
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    drawPageFooter(pdf, i, totalPages, pageWidth, pageHeight);
  }

  onProgress?.('Creating PDF file...');
  return pdf.output('blob');
}

// ===== Excel Generation =====
export function generateExcel(data: ExportData): Blob {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Batch Summary
  const batchSummaryData = [
    ['Microsphere Analysis - Batch Summary'],
    ['Generated', new Date().toLocaleString()],
    [''],
    ['Metric', 'Value'],
    ['Total Images', data.batchAnalysis.totalImages],
    ['Total Detections', data.batchAnalysis.totalDetections],
    ['Confidence Threshold', `${(data.confThreshold * 100).toFixed(0)}%`],
    ['Pixel Calibration', `${data.pixelToUm} μm/pixel`],
    [''],
    ['Class Distribution'],
    ['Class', 'Count', 'Percentage'],
  ];

  Object.entries(data.batchAnalysis.classCounts).forEach(([className, count]) => {
    const percentage = ((count / data.batchAnalysis.totalDetections) * 100).toFixed(1);
    batchSummaryData.push([className, count, `${percentage}%`]);
  });

  batchSummaryData.push(['']);
  batchSummaryData.push(['Per-Class Statistics']);
  batchSummaryData.push(['Class', 'Count', 'Mean (μm)', 'Median (μm)', 'Std (μm)', 'Min (μm)', 'Max (μm)', 'Roundness', 'Convexity', 'Aspect Ratio', 'Solidity']);

  Object.entries(data.batchAnalysis.statsByClass).forEach(([className, stats]) => {
    batchSummaryData.push([
      className,
      stats.count,
      stats.mean_um?.toFixed(2) || 'N/A',
      stats.median_um?.toFixed(2) || 'N/A',
      stats.std_um?.toFixed(2) || 'N/A',
      stats.min_um?.toFixed(2) || 'N/A',
      stats.max_um?.toFixed(2) || 'N/A',
      stats.shape_metrics?.roundness.toFixed(3) || 'N/A',
      stats.shape_metrics?.convexity.toFixed(3) || 'N/A',
      stats.shape_metrics?.aspect_ratio.toFixed(3) || 'N/A',
      stats.shape_metrics?.solidity.toFixed(3) || 'N/A',
    ]);
  });

  const batchSheet = XLSX.utils.aoa_to_sheet(batchSummaryData);
  XLSX.utils.book_append_sheet(workbook, batchSheet, 'Batch Summary');

  // Sheet 2: Per-Image Summary (with shape metrics)
  const perImageHeaders = [
    'Filename', 'Total Detections',
    ...Object.keys(data.batchAnalysis.classCounts),
    'Mean Size (μm)', 'Median Size (μm)', 'Std Size (μm)', 'Min Size (μm)', 'Max Size (μm)',
    'Roundness', 'Convexity', 'Aspect Ratio', 'Solidity'
  ];
  const perImageData: (string | number)[][] = [perImageHeaders];

  data.processedFiles.forEach(file => {
    if (!file.result) return;
    const activeDetections = getActiveDetections(file, data.exclusionsByFile, data.sizeFilter);

    const classCounts: Record<string, number> = {};
    const sizes: number[] = [];

    activeDetections.forEach(det => {
      classCounts[det.class_name] = (classCounts[det.class_name] || 0) + 1;
      if (det.size?.diameter_um) sizes.push(det.size.diameter_um);
    });

    const mean = sizes.length > 0 ? sizes.reduce((a, b) => a + b, 0) / sizes.length : null;
    const sorted = [...sizes].sort((a, b) => a - b);
    const median = sizes.length > 0
      ? (sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)])
      : null;
    const std = sizes.length > 1 && mean !== null
      ? Math.sqrt(sizes.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / (sizes.length - 1))
      : null;
    const min = sizes.length > 0 ? Math.min(...sizes) : null;
    const max = sizes.length > 0 ? Math.max(...sizes) : null;

    // Get shape metrics from the file's statistics (average across all classes)
    const fileStats = file.result.results.statistics;
    let avgRoundness: number | null = null;
    let avgConvexity: number | null = null;
    let avgAspectRatio: number | null = null;
    let avgSolidity: number | null = null;

    if (fileStats?.by_class) {
      const classesWithMetrics = Object.values(fileStats.by_class).filter(
        (s: any) => s.shape_metrics
      );
      if (classesWithMetrics.length > 0) {
        avgRoundness = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.roundness || 0), 0) / classesWithMetrics.length;
        avgConvexity = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.convexity || 0), 0) / classesWithMetrics.length;
        avgAspectRatio = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.aspect_ratio || 0), 0) / classesWithMetrics.length;
        avgSolidity = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.solidity || 0), 0) / classesWithMetrics.length;
      }
    }

    const row: (string | number)[] = [
      file.result.filename,
      activeDetections.length,
      ...Object.keys(data.batchAnalysis.classCounts).map(cls => classCounts[cls] || 0),
      mean?.toFixed(2) || 'N/A',
      median?.toFixed(2) || 'N/A',
      std?.toFixed(2) || 'N/A',
      min?.toFixed(2) || 'N/A',
      max?.toFixed(2) || 'N/A',
      avgRoundness?.toFixed(3) || 'N/A',
      avgConvexity?.toFixed(3) || 'N/A',
      avgAspectRatio?.toFixed(3) || 'N/A',
      avgSolidity?.toFixed(3) || 'N/A',
    ];
    perImageData.push(row);
  });

  const perImageSheet = XLSX.utils.aoa_to_sheet(perImageData);
  XLSX.utils.book_append_sheet(workbook, perImageSheet, 'Per-Image Summary');

  // Sheet 3: All Detections (respects exclusions and size filter)
  const allDetectionsHeaders = ['Filename', 'Detection #', 'Class', 'Confidence', 'Diameter (μm)', 'X1', 'Y1', 'X2', 'Y2'];
  const allDetectionsData: (string | number)[][] = [allDetectionsHeaders];

  data.processedFiles.forEach(file => {
    if (!file.result) return;
    const activeDetections = getActiveDetections(file, data.exclusionsByFile, data.sizeFilter);

    activeDetections.forEach((det, index) => {
      allDetectionsData.push([
        file.result!.filename,
        index + 1,
        det.class_name,
        det.confidence.toFixed(3),
        det.size?.diameter_um?.toFixed(2) || 'N/A',
        Math.round(det.bbox.x1),
        Math.round(det.bbox.y1),
        Math.round(det.bbox.x2),
        Math.round(det.bbox.y2),
      ]);
    });
  });

  const allDetectionsSheet = XLSX.utils.aoa_to_sheet(allDetectionsData);
  XLSX.utils.book_append_sheet(workbook, allDetectionsSheet, 'All Detections');

  // Generate blob
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// ===== CSV Generation =====
export function generateCSVs(data: ExportData): { name: string; content: string }[] {
  const csvFiles: { name: string; content: string }[] = [];

  // CSV 1: Batch Summary
  let batchCSV = 'Metric,Value\n';
  batchCSV += `Total Images,${data.batchAnalysis.totalImages}\n`;
  batchCSV += `Total Detections,${data.batchAnalysis.totalDetections}\n`;
  batchCSV += `Confidence Threshold,${(data.confThreshold * 100).toFixed(0)}%\n`;
  batchCSV += `Pixel Calibration,${data.pixelToUm} um/pixel\n`;
  batchCSV += '\nClass,Count,Percentage\n';

  Object.entries(data.batchAnalysis.classCounts).forEach(([className, count]) => {
    const percentage = ((count / data.batchAnalysis.totalDetections) * 100).toFixed(1);
    batchCSV += `${className},${count},${percentage}%\n`;
  });

  batchCSV += '\nClass,Count,Mean (um),Median (um),Std (um),Min (um),Max (um),Roundness,Convexity,Aspect Ratio,Solidity\n';
  Object.entries(data.batchAnalysis.statsByClass).forEach(([className, stats]) => {
    batchCSV += `${className},${stats.count},${stats.mean_um?.toFixed(2) || 'N/A'},${stats.median_um?.toFixed(2) || 'N/A'},${stats.std_um?.toFixed(2) || 'N/A'},${stats.min_um?.toFixed(2) || 'N/A'},${stats.max_um?.toFixed(2) || 'N/A'},${stats.shape_metrics?.roundness.toFixed(3) || 'N/A'},${stats.shape_metrics?.convexity.toFixed(3) || 'N/A'},${stats.shape_metrics?.aspect_ratio.toFixed(3) || 'N/A'},${stats.shape_metrics?.solidity.toFixed(3) || 'N/A'}\n`;
  });

  csvFiles.push({ name: 'batch_summary.csv', content: batchCSV });

  // CSV 2: Per-Image Summary (with shape metrics)
  const classNames = Object.keys(data.batchAnalysis.classCounts);
  let perImageCSV = `Filename,Total Detections,${classNames.join(',')},Mean Size (um),Median Size (um),Std Size (um),Min Size (um),Max Size (um),Roundness,Convexity,Aspect Ratio,Solidity\n`;

  data.processedFiles.forEach(file => {
    if (!file.result) return;
    const activeDetections = getActiveDetections(file, data.exclusionsByFile, data.sizeFilter);

    const classCounts: Record<string, number> = {};
    const sizes: number[] = [];

    activeDetections.forEach(det => {
      classCounts[det.class_name] = (classCounts[det.class_name] || 0) + 1;
      if (det.size?.diameter_um) sizes.push(det.size.diameter_um);
    });

    const mean = sizes.length > 0 ? sizes.reduce((a, b) => a + b, 0) / sizes.length : null;
    const sorted = [...sizes].sort((a, b) => a - b);
    const median = sizes.length > 0
      ? (sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)])
      : null;
    const std = sizes.length > 1 && mean !== null
      ? Math.sqrt(sizes.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / (sizes.length - 1))
      : null;
    const min = sizes.length > 0 ? Math.min(...sizes) : null;
    const max = sizes.length > 0 ? Math.max(...sizes) : null;

    // Get shape metrics from the file's statistics (average across all classes)
    const fileStats = file.result.results.statistics;
    let avgRoundness: number | null = null;
    let avgConvexity: number | null = null;
    let avgAspectRatio: number | null = null;
    let avgSolidity: number | null = null;

    if (fileStats?.by_class) {
      const classesWithMetrics = Object.values(fileStats.by_class).filter(
        (s: any) => s.shape_metrics
      );
      if (classesWithMetrics.length > 0) {
        avgRoundness = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.roundness || 0), 0) / classesWithMetrics.length;
        avgConvexity = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.convexity || 0), 0) / classesWithMetrics.length;
        avgAspectRatio = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.aspect_ratio || 0), 0) / classesWithMetrics.length;
        avgSolidity = classesWithMetrics.reduce((acc: number, s: any) => acc + (s.shape_metrics?.solidity || 0), 0) / classesWithMetrics.length;
      }
    }

    perImageCSV += `${file.result.filename},${activeDetections.length},${classNames.map(cls => classCounts[cls] || 0).join(',')},${mean?.toFixed(2) || 'N/A'},${median?.toFixed(2) || 'N/A'},${std?.toFixed(2) || 'N/A'},${min?.toFixed(2) || 'N/A'},${max?.toFixed(2) || 'N/A'},${avgRoundness?.toFixed(3) || 'N/A'},${avgConvexity?.toFixed(3) || 'N/A'},${avgAspectRatio?.toFixed(3) || 'N/A'},${avgSolidity?.toFixed(3) || 'N/A'}\n`;
  });

  csvFiles.push({ name: 'per_image_summary.csv', content: perImageCSV });

  // CSV 3: All Detections (respects exclusions and size filter)
  let allDetectionsCSV = 'Filename,Detection #,Class,Confidence,Diameter (um),X1,Y1,X2,Y2\n';

  data.processedFiles.forEach(file => {
    if (!file.result) return;
    const activeDetections = getActiveDetections(file, data.exclusionsByFile, data.sizeFilter);

    activeDetections.forEach((det, index) => {
      allDetectionsCSV += `${file.result!.filename},${index + 1},${det.class_name},${det.confidence.toFixed(3)},${det.size?.diameter_um?.toFixed(2) || 'N/A'},${Math.round(det.bbox.x1)},${Math.round(det.bbox.y1)},${Math.round(det.bbox.x2)},${Math.round(det.bbox.y2)}\n`;
    });
  });

  csvFiles.push({ name: 'all_detections.csv', content: allDetectionsCSV });

  return csvFiles;
}

// ===== Detection Images Export =====
export async function generateDetectionImagesZip(data: ExportData): Promise<Blob> {
  const zip = new JSZip();
  const imagesFolder = zip.folder('detection_images');

  for (const file of data.processedFiles) {
    if (!file.result) continue;

    const excludedIndices = data.exclusionsByFile[file.id] || new Set<number>();

    // Generate annotated image with exclusions and size filter applied
    const annotatedImage = await generateAnnotatedImageWithExclusions(file, excludedIndices, data.sizeFilter);
    if (!annotatedImage) continue;

    // Convert base64 to blob
    const byteCharacters = atob(annotatedImage);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Get filename without extension and add _annotated.png
    const baseName = file.result.filename.replace(/\.[^/.]+$/, '');
    imagesFolder?.file(`${baseName}_annotated.png`, byteArray);
  }

  return await zip.generateAsync({ type: 'blob' });
}

// ===== Main Export Function =====
export async function exportResults(
  data: ExportData,
  options: ExportOptions,
  onProgress?: (message: string) => void
): Promise<void> {
  const selectedFormats: string[] = [];
  if (options.pdf) selectedFormats.push('pdf');
  if (options.excel) selectedFormats.push('excel');
  if (options.csv) selectedFormats.push('csv');
  if (options.detectionImages) selectedFormats.push('images');

  if (selectedFormats.length === 0) {
    throw new Error('No export format selected');
  }

  const timestamp = new Date().toISOString().slice(0, 10);

  // Single format - direct download
  if (selectedFormats.length === 1 && !options.csv) {
    const format = selectedFormats[0];

    if (format === 'pdf') {
      const pdfBlob = await generatePDF(data, options, onProgress);
      saveAs(pdfBlob, `microsphere_analysis_${timestamp}.pdf`);
    } else if (format === 'excel') {
      onProgress?.('Generating Excel...');
      const excelBlob = generateExcel(data);
      saveAs(excelBlob, `microsphere_analysis_${timestamp}.xlsx`);
    } else if (format === 'images') {
      onProgress?.('Generating detection images ZIP...');
      const imagesZip = await generateDetectionImagesZip(data);
      saveAs(imagesZip, `detection_images_${timestamp}.zip`);
    }
    return;
  }

  // Multiple formats or CSV - bundle into ZIP
  onProgress?.('Preparing export bundle...');
  const zip = new JSZip();

  if (options.pdf) {
    const pdfBlob = await generatePDF(data, options, onProgress);
    zip.file(`microsphere_analysis_${timestamp}.pdf`, pdfBlob);
  }

  if (options.excel) {
    onProgress?.('Generating Excel...');
    const excelBlob = generateExcel(data);
    zip.file(`microsphere_analysis_${timestamp}.xlsx`, excelBlob);
  }

  if (options.csv) {
    onProgress?.('Generating CSV files...');
    const csvFiles = generateCSVs(data);
    const csvFolder = zip.folder('csv');
    csvFiles.forEach(csvFile => {
      csvFolder?.file(csvFile.name, csvFile.content);
    });
  }

  if (options.detectionImages) {
    onProgress?.('Adding detection images...');
    const imagesFolder = zip.folder('detection_images');

    for (const file of data.processedFiles) {
      if (!file.result) continue;

      const excludedIndices = data.exclusionsByFile[file.id] || new Set<number>();

      // Generate annotated image with exclusions and size filter applied
      const annotatedImage = await generateAnnotatedImageWithExclusions(file, excludedIndices, data.sizeFilter);
      if (!annotatedImage) continue;

      const byteCharacters = atob(annotatedImage);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const baseName = file.result.filename.replace(/\.[^/.]+$/, '');
      imagesFolder?.file(`${baseName}_annotated.png`, byteArray);
    }
  }

  onProgress?.('Creating ZIP archive...');
  const finalZip = await zip.generateAsync({ type: 'blob' });
  saveAs(finalZip, `microsphere_export_${timestamp}.zip`);
}
